import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { Navigation } from '../components/Navigation';
import type { QueryClient } from '@tanstack/react-query';
import type { User } from '@supabase/supabase-js';
import { ActionBarProvider } from '@/lib/context/ActionBarContext';
import { useEffect, useRef } from 'react';

// Define router context type
interface RouterContext {
  auth: {
    user: User | null;
    isAuthenticated: boolean;
  };
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  // Hide navigation on login page
  const showNavigation = currentPath !== '/login';
  const mainRef = useRef<HTMLElement>(null);

  /**
   * iOS PWA Keyboard Scroll Fix
   *
   * Problem: On iOS PWAs, when the virtual keyboard opens, iOS pushes the page content
   * up to keep the focused input visible. When the keyboard closes, iOS doesn't properly
   * restore the scroll position, leaving the content "stuck" in the pushed-up state.
   * Users cannot scroll back to the top of the page (e.g., to reach the back button).
   *
   * Solution: Use the visualViewport API to detect when the keyboard closes (viewport
   * height increases significantly), then reset `window.scrollTo(0, 0)` to unlock the
   * scroll. We preserve and restore the main content scroll position so users don't
   * lose their place in forms.
   *
   * The 100px threshold ensures we only trigger on actual keyboard close events,
   * not minor viewport adjustments.
   */
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let previousHeight = viewport.height;

    const handleResize = () => {
      const currentHeight = viewport.height;
      // Keyboard closed - viewport got taller
      if (currentHeight > previousHeight + 100) {
        const mainScroll = mainRef.current?.scrollTop ?? 0;
        window.scrollTo(0, 0);
        if (mainRef.current) {
          mainRef.current.scrollTop = mainScroll;
        }
      }
      previousHeight = currentHeight;
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ActionBarProvider>
      <div className="flex flex-col h-full overflow-hidden">
        <main
          ref={mainRef}
          id="app-scroll-container"
          className="flex-1 overflow-y-auto"
        >
          <Outlet />
        </main>
        {showNavigation && <Navigation />}
      </div>
    </ActionBarProvider>
  );
}
